import { Command, Search, LogOut, User as UserIcon, Loader2, Sun, Moon, Monitor } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Button } from '@/components/ui/Button.js';
import { NotificationsBell } from '@/components/shell/NotificationsBell.js';
import { useAuth } from '@/lib/auth.js';
import { useTheme, type ThemePreference } from '@/lib/theme.js';
import { cn } from '@/lib/utils.js';

export function TopBar(): JSX.Element {
  return (
    <div className="flex h-14 items-center justify-between px-6">
      <OrgSwitcher />
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="flex h-8 items-center gap-2 rounded-md border border-border bg-background/60 px-2.5 text-xs text-muted-foreground hover:bg-accent/60"
        >
          <Search className="h-3.5 w-3.5" />
          <span>Search or run a command</span>
          <span className="ml-3 flex items-center gap-0.5 rounded border border-border bg-card px-1 py-0.5 font-mono text-[10px]">
            <Command className="h-2.5 w-2.5" />K
          </span>
        </button>
        <NotificationsBell />
        <ThemeToggle />
        <AccountMenu />
      </div>
    </div>
  );
}

function ThemeToggle(): JSX.Element {
  const { preference, setPreference } = useTheme();

  const options: Array<{ value: ThemePreference; label: string; icon: React.ComponentType<{ className?: string | undefined }> }> = [
    { value: 'system', label: 'System', icon: Monitor },
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
  ];
  const current = options.find((o) => o.value === preference) ?? options[0]!;
  const Icon = current.icon;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Theme: ${current.label}`}
          title={`Theme: ${current.label}`}
        >
          <Icon className="h-4 w-4" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="min-w-[140px] rounded-md border border-border bg-card p-1 text-sm shadow-lg"
        >
          {options.map((opt) => {
            const OptIcon = opt.icon;
            return (
              <DropdownMenu.Item
                key={opt.value}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 outline-none focus:bg-accent',
                  preference === opt.value && 'text-foreground',
                )}
                onSelect={(e) => {
                  e.preventDefault();
                  setPreference(opt.value);
                }}
              >
                <OptIcon className="h-3.5 w-3.5" />
                {opt.label}
                {preference === opt.value && (
                  <span className="ml-auto text-[10px] text-muted-foreground">active</span>
                )}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function OrgSwitcher(): JSX.Element {
  const { memberships } = useAuth();
  if (memberships.length === 0) {
    return <span className="text-sm text-muted-foreground">No organisation</span>;
  }
  const active = memberships[0]!;
  return (
    <button className="flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-accent/60">
      <div className="h-5 w-5 rounded bg-primary/20" />
      <span className="font-medium">{active.orgName}</span>
      {memberships.length > 1 && (
        <span className="text-xs text-muted-foreground">+{memberships.length - 1} more</span>
      )}
    </button>
  );
}

function AccountMenu(): JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  async function handleLogout() {
    setSigningOut(true);
    try {
      await logout();
      await navigate({ to: '/login' });
    } finally {
      setSigningOut(false);
    }
  }

  const label = user?.name ?? user?.email ?? 'Account';

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <span
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold uppercase text-primary',
            )}
          >
            {initials(label)}
          </span>
          <span className="hidden sm:inline">{label}</span>
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="min-w-[200px] rounded-md border border-border bg-card p-1 text-sm shadow-lg"
        >
          {user && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              <div className="text-foreground">{user.name}</div>
              <div className="font-mono">{user.email}</div>
            </div>
          )}
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 outline-none focus:bg-accent"
            onSelect={(e) => {
              e.preventDefault();
              void navigate({ to: '/account' });
            }}
          >
            <UserIcon className="h-3.5 w-3.5" />
            Account
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 outline-none focus:bg-accent"
            onSelect={(e) => {
              e.preventDefault();
              void handleLogout();
            }}
          >
            {signingOut ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}
            Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
